import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import { fromDateTime, toDateTime } from './utils';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
dayjs.extend(utcPlugin);
import Input from './Input';
const pointIcon = () => _jsx("svg", { viewBox: "0 0 24 24", children: _jsx("circle", { fill: 'currentColor', cx: "12", cy: "12", r: "3" }) });
export default React.memo(function DateTimeInput(props) {
    const [value, setValue] = React.useState('');
    const [isValid, setIsValid] = React.useState(true);
    useEffect(() => {
        if (!props.value) {
            setValue('');
            return;
        }
        let dateTime;
        if (props.utcMode) {
            dateTime = dayjs.utc(props.value);
        }
        else {
            dateTime = dayjs(props.value);
        }
        const { date, time } = toDateTime(dateTime.toISOString());
        const _value = `${props.type === 'time'
            ? time
            : props.type === 'date'
                ? date
                : (props.type === 'full' || !props.type) && `${date}, ${time}`}`;
        setValue(_value);
    }, [props.value, props.type]);
    useEffect(() => {
        if (props.onInvalid && !isValid)
            props.onInvalid();
    }, [isValid]);
    const daysInMonth = (month, year) => {
        if (month === 2) {
            return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0 ? 29 : 28;
        }
        return 30 + [1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1][month - 1];
    };
    const fillNumber = (v, digits = 2) => {
        return v < Math.pow(10, digits) ?
            `${'0'.repeat(digits - v.toString().length)}${v}` :
            v.toString();
    };
    const fitToRange = (value, min = 0, max = 1) => {
        return Math.max(min, Math.min(max, value));
    };
    const validateAndSetValue = (v) => {
        const timeZoneOffset = (new Date(props.value)).getTimezoneOffset();
        const { type } = props;
        const _d = dayjs(props.value);
        let dateTime = _d.isValid() ? _d : dayjs();
        let isBC = false;
        if (props.utcMode) {
            dateTime = dateTime.add(timeZoneOffset, 'minute');
        }
        if (v.match(/[bB]/)) {
            isBC = true;
            v = v.replace(/[bB]/g, '');
        }
        const { date, time } = toDateTime(dateTime.toISOString());
        const [hours, minutes, seconds] = time.split(':');
        const _currTime = (`${hours}:${minutes}:${props.hideSeconds ? '' : seconds}`).trim();
        let result = '';
        if (type === 'date') {
            result = fromDateTime(v, _currTime, isBC);
        }
        else if (type === 'time') {
            result = fromDateTime(date, v);
        }
        else if (type === 'full') {
            const [date, time] = v.split(', ');
            result = fromDateTime(date, time, isBC);
        }
        const isValid = dayjs(result).isValid();
        isValid && props.onChange(result, isBC);
        setIsValid(isValid);
        setValue(v);
    };
    const adjustDate = (date) => {
        const separator = '.';
        const toks = date.split(separator);
        const values = toks.map((t, i) => {
            if (i === 2 && props.useBC) {
                const _num = t.replace(/[\D]/g, '');
                if (_num.length === 4) {
                    if (t.endsWith('_'))
                        t = t.replace(/[\D]/g, '');
                }
                return +t;
            }
            else {
                return +t;
            }
        });
        values.forEach((v, i) => {
            if (!isNaN(v)) {
                if (i == 2) {
                    toks[i] = fillNumber(fitToRange(values[i], (props.useBC ? 1 : (props.minYear || 1200)), (props.maxYear || 2400)), 4);
                }
                else if (i == 1) {
                    toks[i] = fillNumber(fitToRange(values[i], 1, 12), 2);
                }
                else {
                    let max = isNaN(+values[1]) || isNaN(values[2]) ? 31 : daysInMonth(values[1], values[2]) || 31;
                    toks[i] = fillNumber(fitToRange(values[i], 1, max), 2);
                }
            }
        });
        return toks.join(separator).replace(/[\s]/g, '');
    };
    const adjustTime = (time) => {
        const separator = ':';
        const toks = time.split(separator);
        if (props.hideSeconds) {
            toks.push('00');
        }
        const values = toks.map(t => +t);
        values.forEach((v, i) => {
            if (!isNaN(v)) {
                toks[i] = fillNumber(fitToRange(values[i], 0, i == 0 ? 23 : 59), 2);
            }
        });
        return toks.join(separator);
    };
    const onChange = (v) => {
        const { type } = props;
        if (type == 'date') {
            v = adjustDate(v);
        }
        else if (type == 'time') {
            v = adjustTime(v);
        }
        else if (type == 'full') {
            const [date, time] = v.replace(/\s/g, '').split(',');
            v = `${adjustDate(date.trim())}, ${adjustTime(time.trim())}`;
        }
        validateAndSetValue(v);
    };
    const onBlur = (e) => {
        var _a;
        if ((e === null || e === void 0 ? void 0 : e.target) && props.type === 'time') {
            const value = e.target.value;
            const sanitizedValue = value.split(':')
                .map(token => {
                const numValue = +token;
                return isNaN(numValue) ? '00' : token;
            })
                .join(':');
            validateAndSetValue(`${sanitizedValue}${props.hideSeconds ? ':00' : ''}`);
        }
        (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props, e);
    };
    const Icon = isValid && !!props.icon ? props.icon : pointIcon;
    const timeFormat = props.hideSeconds
        ? props.isTimeControl ? '99 : 99' : '99:99'
        : props.isTimeControl ? '99 : 99 : 99' : '99:99:99';
    const mask = {
        date: props.isTimeControl ? (props.useBC ? '99 . 99 . 9999a' : '99 . 99 . 9999') : (props.useBC ? '99.99.9999a' : '99.99.9999'),
        time: timeFormat,
        full: props.hideSeconds ? '99.99.9999, 99:99' : '99.99.9999, 99:99:99'
    };
    return _jsx(Input, { inputmode: props.inputmode || 'text', size: props.size || 'medium', mode: props.mode || 'normal', className: `${props.className || ''} date-time-input`, mask: !props.disabled ? mask[props.type] : '', value: value, onChange: onChange, onFocus: props.onFocus, onBlur: onBlur, icon: !props.isTimeControl && !props.hideIcon ? _jsx(Icon, {}) : null, autoFocus: !!props.autoFocus, isTimeControl: props.isTimeControl, disabled: props.disabled, selectBeginning: props.selectBeginning, options: props.options });
});
